import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CustomIcon, { IconType } from 'src/components/shared/custom-icon/custom-icon';
import Loader from 'src/components/shared/loader/loader';
import { ProtectPermissions } from 'src/components/shared/protect-permissions/protect-permissions';
import advancedFeaturesService, { AdvancedFeatureStateDto } from 'src/services/advanced-features.service';
import {
    ActivationState,
    BulkProtectionOptions,
    ComponentNamesForPermission,
    MainPages,
    ProtectionMenuNameOptions,
    ProtectionPlusTitles,
    SavingStatus
} from 'src/utils/enums';
import activationService from 'src/services/activation.service';
import { useActivation } from 'src/utils/activationUtil';
import { toCamelCase } from 'src/utils/string-normalization';
import usePromiseFeedback from 'src/components/shared/promise-feedback/usePromiseFeedback';
import { useStores } from 'src/components/store';
import {
    dnsProtectionFeature,
    dynamicDefenseShieldFeature,
    spywareProtectionFeature,
    ThreatFeatureStructure,
    virusProtectionFeature,
    vulnerabilityProtectionFeature
} from 'src/components/features/protection-plus/protection-plus-config';
import './protection-plus.scss';
import { ThreatFeature } from 'src/components/features/protection-plus/threat-feature';
import { usePageViewEvent } from '../../shared/hooks/use-page-view-event';

export const ProtectionPlusOptionInfo: React.FC<{ icon: IconType, title: string, explain: string, additionalExplain?: JSX.Element }> =
    ({ icon, title, explain, additionalExplain }) => {
        return <div className="protection-plus-option-info">
            <span className="protection-plus-icon"><CustomIcon type={icon} /></span>
            <div className="title-and-explain">
                <div className="title-option">{title}</div>
                <div className="explain">{explain}{additionalExplain}</div>
            </div>
        </div>;
    };

export const ProtectionPlus: React.FC = observer(() => {
    const { fwVersionStore } = useStores();
    const [features, setFeatures] = useState<Record<string, string>>({});
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const { getPageType } = useActivation();

    usePageViewEvent(MainPages.POLICY, ProtectionMenuNameOptions.PROTECTION_PLUS);

    useEffect(() => {
        fwVersionStore.setProtectionType(ProtectionMenuNameOptions.PROTECTION_PLUS);
    }, []);

    useEffect(() => {
        setFeatures(fwVersionStore.getProtectionPlusFeatures);
    }, [fwVersionStore.getProtectionPlusFeatures]);

    const onChange = (selectedValue: string | boolean, threatItem: ThreatFeatureStructure) => {
        if (threatItem.type === 'toggle') {
            handleToggleChange(selectedValue as boolean);
            return;
        }
        handleSelectChange(selectedValue, threatItem);
    };

    const handleSelectChange = useCallback((selectedValue: string | boolean, threatItem: ThreatFeatureStructure) => {
        if (!threatItem.nameForUpdate) return;
        const valueToSet = selectedValue as string;
        setFeatures({ ...features, [threatItem.nameForUpdate]: valueToSet });
        const updateDto: AdvancedFeatureStateDto = {
            featureType: threatItem.nameForUpdate,
            blocked: valueToSet === 'Block',
            enabled: true
        };
        promiseFeedbackFactory({
            promise: fwVersionStore.genericCrudMethod(advancedFeaturesService.updateAdvancedFeatures, [updateDto]),
            successCallback: () => fwVersionStore.setSavingStatus(SavingStatus.Saved),
            errorCallback: () => setFeatures(fwVersionStore.getProtectionPlusFeatures)
        });
    }, [features]);

    const handleToggleChange = (value: boolean) => {
        const pageType = getPageType(BulkProtectionOptions.PROTECTION_PLUS);

        promiseFeedbackFactory({
            promise: fwVersionStore.genericCrudMethod(activationService.updatePageActivation, [{
                pageType: pageType,
                activationState: value ? ActivationState.ACTIVE : ActivationState.INACTIVE
            }]),
            notifObj: { show: true, notificationMsg: `Dynamic Defense Shield ${toCamelCase(ActivationState[value ? 'ACTIVE':'INACTIVE'])}` },
            successCallback: () => fwVersionStore.setSavingStatus(SavingStatus.Saved),
        });
    };

    const disableReason = useMemo(() => fwVersionStore.getDisableReason, [fwVersionStore.getDisableReason]);

    const globalFeatures = [dynamicDefenseShieldFeature, dnsProtectionFeature];

    const advancedFeatures = [
            virusProtectionFeature,
            spywareProtectionFeature,
            vulnerabilityProtectionFeature];

    return <div className="page-content protection-plus" data-testid="service-control">
        <div className="page-content-wrapper" data-testid="page-content">
            {(fwVersionStore.isLoading || !fwVersionStore.selectedFwVersion) && <Loader type="full-screen" />}
            <div className="protection-plus-group global">
                <div className="title-group">AT&T Global Protection</div>
                {globalFeatures.map((feature) => (
                    <ThreatFeature
                        key={feature.key}
                        feature={feature}
                        features={features}
                        hasPermissions={true}
                        disableReason={disableReason ?? ''}
                        onChange={onChange}
                    />
                ))}
            </div>
            <ProtectPermissions componentName={ComponentNamesForPermission.AdvancedThreatProtection}>
                {(hasPermissions: boolean, UpgradeButton?: () => JSX.Element) => <div className="protection-plus-group">
                    <div className="title-group-with-upgrade-button">
                        <div className="title-group">{ProtectionPlusTitles.ADVANCED_TREAT}</div>
                        {UpgradeButton ? <UpgradeButton /> : null}
                    </div>
                    {advancedFeatures.map((feature) => (
                        <ThreatFeature
                            key={feature.key}
                            feature={feature}
                            features={features}
                            hasPermissions={hasPermissions}
                            disableReason={disableReason ?? ''}
                            onChange={onChange}
                        />
                    ))}
                </div>}
            </ProtectPermissions>
        </div>
        {promiseContextHolder}
    </div >;
});